'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { isEqual } from 'radash';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import { automationId } from '@terra/shared-utils';
import { IconButton } from '../../../components/icon-button';
import { Collapsible } from '../../../components/collapsible';
import { useDomainTreeCollapsed } from '../../../hooks/useDomainTreeCollapsed';
import { ArrowRight01Icon } from '@terra/ui-icons';
import {
  getDescendantIds,
  getAncestorIds,
  getDomainTreeStructureSignature,
  toggleNodeExpanded,
  expandAllNodes,
  collapseAllNodes,
  type DomainTree,
  type DomainTreeNode,
} from '../utils/domain-tree.utils';
import { cloneDomainTree } from '../utils/hydrate-domain-tree-from-dto';
import {
  buildDomainTreeRootScopeMap,
  isDomainTreeNodeVisibleForListFilter,
} from '../utils/domain-tree-archived-roots';
import { DomainTreeContent } from './DomainTreeContent';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';

const EMPTY_DOMAIN_TREE: DomainTree = { roots: [], nodeMap: new Map() };
const getDomainDisplayName = (
  node: DomainTreeNode | undefined,
  fallbackId?: string,
): string => {
  if (!node) {
    return fallbackId || '';
  }

  return node.fullDomain || node.name || fallbackId || '';
};

const getDomainDisplayText = (
  selectedDomains: string[],
  domainTree: DomainTree,
  tFilters: (
    key: string,
    params?: {
      count: number;
    },
  ) => string,
): string | undefined => {
  if (selectedDomains.length === 0) {
    return undefined;
  }

  if (selectedDomains.length === 1) {
    const selectedNode = domainTree.nodeMap.get(selectedDomains[0]);

    return getDomainDisplayName(selectedNode, selectedDomains[0]);
  }

  return tFilters('selectedCount', { count: selectedDomains.length });
};

const getExpandedNodeIds = (domainTree: DomainTree): string[] =>
  Array.from(domainTree.nodeMap.values())
    .filter((node) => node.isExpanded)
    .map((node) => node.id);
const applyExpandedNodes = (
  tree: DomainTree,
  expandedNodeIds: string[],
): DomainTree => {
  const expandedSet = new Set(expandedNodeIds);

  tree.nodeMap.forEach((node) => {
    node.isExpanded = expandedSet.has(node.id);
  });

  return tree;
};

const mergeExpansionFromPreviousTree = ({
  newTree,
  prevTree,
}: {
  newTree: DomainTree;
  prevTree: DomainTree;
}): DomainTree => {
  newTree.nodeMap.forEach((newNode, nodeId) => {
    const existingNode = prevTree.nodeMap.get(nodeId);

    if (existingNode) {
      newNode.isExpanded = existingNode.isExpanded;
    }
  });

  return newTree;
};

const getSelectionExcludingSubtree = ({
  node,
  roots,
  isNodeSelectable,
  boundaryNodeId,
}: {
  node: DomainTreeNode;
  roots: DomainTreeNode[];
  isNodeSelectable?: (node: DomainTreeNode) => boolean;
  boundaryNodeId?: string;
}): string[] => {
  const selectedNodeIds: string[] = [];
  let currentNode = node;

  while (currentNode.parent) {
    const currentNodeId = currentNode.id;

    selectedNodeIds.push(
      ...currentNode.parent.children
        .filter((sibling) => sibling.id !== currentNodeId)
        .filter((sibling) => !isNodeSelectable || isNodeSelectable(sibling))
        .map((sibling) => sibling.id),
    );
    currentNode = currentNode.parent;

    if (currentNode.id === boundaryNodeId) {
      return selectedNodeIds;
    }
  }

  selectedNodeIds.push(
    ...roots
      .filter((root) => root.id !== currentNode.id)
      .filter((root) => !isNodeSelectable || isNodeSelectable(root))
      .map((root) => root.id),
  );

  return selectedNodeIds;
};

export type DomainTreePanelProps = {
  domainTree: DomainTree | null;
  selectedDomains: string[];
  onDomainSelectionChange: (
    selectedDomains: string[],
    displayText?: string,
  ) => void;
  nodeIdToCountMap?: Record<string, number>;
  selectionMode?: 'multiple' | 'single';
  fullWidth?: boolean;
  expandedNodes?: string[];
  onExpandedNodesChange?: (expandedNodes: string[]) => void;
  disableCollapsible?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  isNodeSelectable?: (node: DomainTreeNode) => boolean;
  listLifecycleState?: LifecycleStateFilterDto;
  dataTestid?: string;
  searchTerm?: string;
  onSearchTermChange?: (searchTerm: string) => void;
  showHeader?: boolean;
  showSearch?: boolean;
  hideZeroCountNodes?: boolean;
  cascadeSelectionState?: boolean;
  allNodesSelected?: boolean;
};
export function DomainTreePanel({
  domainTree: domainTreeProp,
  selectedDomains,
  onDomainSelectionChange,
  nodeIdToCountMap,
  fullWidth = false,
  selectionMode = 'multiple',
  expandedNodes: controlledExpandedNodes,
  onExpandedNodesChange,
  disableCollapsible = false,
  onRefresh,
  isRefreshing = false,
  isLoading = false,
  isNodeSelectable,
  listLifecycleState,
  dataTestid = '',
  searchTerm: controlledSearchTerm,
  onSearchTermChange,
  showHeader = true,
  showSearch = true,
  hideZeroCountNodes = false,
  cascadeSelectionState = false,
  allNodesSelected = false,
}: DomainTreePanelProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const tFilters = useTranslations('designSystem.applicationAssets.filters');
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const searchTerm = controlledSearchTerm ?? internalSearchTerm;
  const setSearchTerm = useCallback(
    (nextSearchTerm: string) => {
      if (controlledSearchTerm === undefined) {
        setInternalSearchTerm(nextSearchTerm);
      }

      onSearchTermChange?.(nextSearchTerm);
    },
    [controlledSearchTerm, onSearchTermChange],
  );
  const { isCollapsed, setIsDomainTreeCollapsed, isCollapsible } =
    useDomainTreeCollapsed(disableCollapsible);
  const onToggleCollapse = useCallback(() => {
    setIsDomainTreeCollapsed(!isCollapsed);
  }, [isCollapsed, setIsDomainTreeCollapsed]);
  const hasControlledExpandedNodes = controlledExpandedNodes !== undefined;
  const [domainTree, setDomainTree] = useState<DomainTree>(EMPTY_DOMAIN_TREE);
  const latestDomainTreeRef = useRef(domainTree);

  latestDomainTreeRef.current = domainTree;
  const rootScopeByRootId = useMemo(
    () => buildDomainTreeRootScopeMap(domainTree.roots),
    [domainTree],
  );
  const canSelectNode = useCallback(
    (node: DomainTreeNode) => {
      if (
        !isDomainTreeNodeVisibleForListFilter({
          node,
          listLifecycleState,
          rootScopeByRootId,
        })
      ) {
        return false;
      }

      return isNodeSelectable ? isNodeSelectable(node) : true;
    },
    [isNodeSelectable, listLifecycleState, rootScopeByRootId],
  );
  const prevDepsRef = useRef<{
    domainTreeStructureSignature: string | null;
    hasControlledExpandedNodes: typeof hasControlledExpandedNodes;
    controlledExpandedNodes: typeof controlledExpandedNodes;
  } | null>(null);

  useEffect(() => {
    if (!domainTreeProp) {
      setDomainTree(EMPTY_DOMAIN_TREE);

      return;
    }

    const domainTreeStructureSignature =
      getDomainTreeStructureSignature(domainTreeProp);

    if (prevDepsRef.current) {
      const prev = prevDepsRef.current;
      const shouldUpdate =
        prev.domainTreeStructureSignature !== domainTreeStructureSignature ||
        prev.hasControlledExpandedNodes !== hasControlledExpandedNodes ||
        !isEqual(prev.controlledExpandedNodes, controlledExpandedNodes);

      if (!shouldUpdate) {
        return;
      }
    }

    prevDepsRef.current = {
      domainTreeStructureSignature,
      hasControlledExpandedNodes,
      controlledExpandedNodes,
    };
    setDomainTree((prevTree) => {
      const clonedTree = cloneDomainTree(domainTreeProp);

      if (hasControlledExpandedNodes) {
        return applyExpandedNodes(clonedTree, controlledExpandedNodes ?? []);
      }

      if (prevTree.nodeMap.size > 0) {
        return mergeExpansionFromPreviousTree({
          newTree: clonedTree,
          prevTree,
        });
      }

      return clonedTree;
    });
  }, [domainTreeProp, hasControlledExpandedNodes, controlledExpandedNodes]);
  const handleToggleSelection = useCallback(
    (nodeId: string, checked: boolean) => {
      const node = domainTree.nodeMap.get(nodeId);

      if (!node) return;
      if (!canSelectNode(node)) return;
      if (selectionMode === 'single') {
        const next = checked ? [nodeId] : [];
        const displayText = getDomainDisplayText(next, domainTree, tFilters);

        onDomainSelectionChange(next, displayText);

        return;
      }

      const descendantIds = new Set(getDescendantIds(node));
      const nodeAncestorIds = getAncestorIds(node);
      const ancestorIds = new Set(nodeAncestorIds);
      const selectedAncestorId = cascadeSelectionState
        ? nodeAncestorIds.find((id) => selectedDomains.includes(id))
        : undefined;
      let newSelectedDomains: string[];

      if (checked) {
        const withoutDescendantsOrAncestors = selectedDomains.filter(
          (id) => !descendantIds.has(id) && !ancestorIds.has(id),
        );

        newSelectedDomains = withoutDescendantsOrAncestors.includes(nodeId)
          ? withoutDescendantsOrAncestors
          : [...withoutDescendantsOrAncestors, nodeId];
      } else if (allNodesSelected && selectedDomains.length === 0) {
        newSelectedDomains = getSelectionExcludingSubtree({
          node,
          roots: domainTree.roots,
          isNodeSelectable,
        });
      } else if (selectedAncestorId) {
        newSelectedDomains = [
          ...selectedDomains.filter((id) => id !== selectedAncestorId),
          ...getSelectionExcludingSubtree({
            node,
            roots: domainTree.roots,
            isNodeSelectable,
            boundaryNodeId: selectedAncestorId,
          }),
        ];
      } else {
        newSelectedDomains = selectedDomains.filter(
          (id) =>
            id !== nodeId &&
            !descendantIds.has(id) &&
            (!cascadeSelectionState || !ancestorIds.has(id)),
        );
      }

      const displayText = getDomainDisplayText(
        newSelectedDomains,
        domainTree,
        tFilters,
      );

      onDomainSelectionChange(newSelectedDomains, displayText);
    },
    [
      selectedDomains,
      onDomainSelectionChange,
      domainTree,
      canSelectNode,
      isNodeSelectable,
      selectionMode,
      cascadeSelectionState,
      allNodesSelected,
      tFilters,
    ],
  );

  useEffect(() => {
    if (domainTree.nodeMap.size === 0) {
      return;
    }

    const nextSelectedDomains = selectedDomains.filter((nodeId) => {
      const node = domainTree.nodeMap.get(nodeId);

      return node !== undefined && canSelectNode(node);
    });

    if (nextSelectedDomains.length === selectedDomains.length) {
      return;
    }

    onDomainSelectionChange(
      nextSelectedDomains,
      getDomainDisplayText(nextSelectedDomains, domainTree, tFilters),
    );
  }, [
    canSelectNode,
    domainTree,
    onDomainSelectionChange,
    selectedDomains,
    tFilters,
  ]);
  const handleToggleExpanded = useCallback(
    (nodeId: string) => {
      if (onExpandedNodesChange) {
        const current = hasControlledExpandedNodes
          ? (controlledExpandedNodes ?? [])
          : getExpandedNodeIds(latestDomainTreeRef.current);
        const next = current.includes(nodeId)
          ? current.filter((id) => id !== nodeId)
          : [...current, nodeId];

        onExpandedNodesChange(next);
      }

      if (!hasControlledExpandedNodes) {
        setDomainTree((prevTree) => toggleNodeExpanded(prevTree, nodeId));
      }
    },
    [
      hasControlledExpandedNodes,
      controlledExpandedNodes,
      onExpandedNodesChange,
    ],
  );
  const handleExpandAll = useCallback(() => {
    const allExpandableNodes = Array.from(domainTree.nodeMap.values())
      .filter((node) => node.children?.length)
      .map((node) => node.id);

    if (onExpandedNodesChange) {
      onExpandedNodesChange(allExpandableNodes);
    }

    if (!hasControlledExpandedNodes) {
      setDomainTree((prevTree) => expandAllNodes(prevTree));
    }
  }, [hasControlledExpandedNodes, onExpandedNodesChange, domainTree.nodeMap]);
  const handleCollapseAll = useCallback(() => {
    if (onExpandedNodesChange) {
      onExpandedNodesChange([]);
    }

    if (!hasControlledExpandedNodes) {
      setDomainTree((prevTree) => collapseAllNodes(prevTree));
    }
  }, [hasControlledExpandedNodes, onExpandedNodesChange]);
  const selectedCountInTree = selectedDomains.filter((id) =>
    domainTree.nodeMap.has(id),
  ).length;
  const hasActiveFilter = selectedCountInTree > 0 || searchTerm.length > 0;

  return (
    <Box
      height="full"
      borderRightWidth="1px"
      borderColor="border.subtle"
      bg="bg.canvas"
      {...automationId(dataTestid)}
    >
      {fullWidth ? (
        <DomainTreeContent
          domainTree={domainTree}
          selectedDomains={selectedDomains}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleExpandAll={handleExpandAll}
          handleCollapseAll={handleCollapseAll}
          handleToggleSelection={handleToggleSelection}
          handleToggleExpanded={handleToggleExpanded}
          nodeIdToCountMap={nodeIdToCountMap}
          isNodeSelectable={canSelectNode}
          listLifecycleState={listLifecycleState}
          onToggleCollapse={isCollapsible ? onToggleCollapse : undefined}
          hasActiveFilter={hasActiveFilter}
          selectionMode={selectionMode}
          disableCollapsible={!isCollapsible}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
          isLoading={isLoading}
          showHeader={showHeader}
          showSearch={showSearch}
          hideZeroCountNodes={hideZeroCountNodes}
          cascadeSelectionState={cascadeSelectionState}
          allNodesSelected={allNodesSelected}
        />
      ) : (
        <Collapsible.Root
          open={!isCollapsed}
          onOpenChange={isCollapsible ? () => onToggleCollapse() : undefined}
          height="full"
        >
          <Box
            w={isCollapsed ? '40px' : '400px'}
            height="full"
            transition="width 0.3s ease-in-out"
          >
            {isCollapsed ? (
              <Box
                height="full"
                display="flex"
                alignItems="flex-start"
                justifyContent="center"
                pt={4}
              >
                <Box position="relative">
                  <Collapsible.Trigger asChild>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      color="fg.muted"
                      aria-label={t('filters.expandDomainTree')}
                    >
                      <ArrowRight01Icon size={18} />
                    </IconButton>
                  </Collapsible.Trigger>
                  {hasActiveFilter && (
                    <Box
                      position="absolute"
                      top={0}
                      right={0}
                      w="6px"
                      h="6px"
                      bg="colorPalette.emphasized"
                      borderRadius="full"
                      borderWidth="1px"
                      borderColor="bg.canvas"
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Collapsible.Content height="full">
                <Box w="400px" height="full">
                  <DomainTreeContent
                    domainTree={domainTree}
                    selectedDomains={selectedDomains}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    handleExpandAll={handleExpandAll}
                    handleCollapseAll={handleCollapseAll}
                    handleToggleSelection={handleToggleSelection}
                    handleToggleExpanded={handleToggleExpanded}
                    nodeIdToCountMap={nodeIdToCountMap}
                    isNodeSelectable={canSelectNode}
                    listLifecycleState={listLifecycleState}
                    onToggleCollapse={
                      isCollapsible ? onToggleCollapse : undefined
                    }
                    hasActiveFilter={hasActiveFilter}
                    selectionMode={selectionMode}
                    disableCollapsible={!isCollapsible}
                    onRefresh={onRefresh}
                    isRefreshing={isRefreshing}
                    isLoading={isLoading}
                    showHeader={showHeader}
                    showSearch={showSearch}
                    hideZeroCountNodes={hideZeroCountNodes}
                    cascadeSelectionState={cascadeSelectionState}
                    allNodesSelected={allNodesSelected}
                  />
                </Box>
              </Collapsible.Content>
            )}
          </Box>
        </Collapsible.Root>
      )}
    </Box>
  );
}
